import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  MultiSelect,
  ScrollArea,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconUser } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPublicProfiles, sortPublicProfiles } from '../lib/publicProfiles';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../lib/auth/useAuth';
import { PageHeader } from '../components/PageHeader';
import { EmptyState } from '../components/EmptyState';

type Role = { id: string; name: string };
type Ressort = { id: string; name: string };

type MemberDetail = {
  role_ids: string[];
  ressort_ids: string[];
  can_manage_events: boolean;
  can_manage_costumes: boolean;
  is_admin: boolean;
  is_dancer: boolean | null;
};

type MemberRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  nickname: string | null;
  rolle: string | null;
  ressort: string | null;
  is_dancer: boolean | null;
  avatar_url: string | null;
};

function displayName(m: MemberRow) {
  const fn = (m.first_name ?? '').trim();
  const ln = (m.last_name ?? '').trim();
  const nn = (m.nickname ?? '').trim();
  const base = [fn, ln].filter(Boolean).join(' ');
  return nn ? `${base} (${nn})` : base || m.id;
}

function initials(m: MemberRow) {
  const fn = (m.first_name ?? '').trim();
  const ln = (m.last_name ?? '').trim();
  const a = fn ? fn[0] : '';
  const b = ln ? ln[0] : '';
  const out = (a + b).toUpperCase();
  return out || m.id.slice(0, 2).toUpperCase();
}

function norm(s: string) {
  return s.trim().toLowerCase();
}

export function MembersPage() {
  const qc = useQueryClient();
  const { refreshCapabilities, session, capabilities } = useAuth();

  useEffect(() => {
    document.title = 'Mitglieder – Musical!Kultur Admin';
  }, []);

  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');

  const [draftRoleIds, setDraftRoleIds] = useState<string[] | null>(null);
  const [draftRessortIds, setDraftRessortIds] = useState<string[] | null>(null);
  const [draftCanManageEvents, setDraftCanManageEvents] = useState<boolean | null>(null);
  const [draftCanManageCostumes, setDraftCanManageCostumes] = useState<boolean | null>(null);
  const [draftIsAdmin, setDraftIsAdmin] = useState<boolean | null>(null);
  const [draftIsDancer, setDraftIsDancer] = useState<boolean | null>(null);

  const rolesQ = useQuery({
    queryKey: ['roles'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Role[]> => {
      const { data, error } = await supabase.from('roles').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as Role[];
    },
  });

  const ressortsQ = useQuery({
    queryKey: ['ressorts'],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Ressort[]> => {
      const { data, error } = await supabase.from('ressorts').select('id,name').order('name');
      if (error) throw error;
      return (data ?? []) as Ressort[];
    },
  });

  const membersQ = useQuery({
    queryKey: ['members', 'public_profiles'],
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<MemberRow[]> => {
      return sortPublicProfiles(
        (await fetchPublicProfiles()).map((row) => ({
          id: row.id,
          first_name: row.first_name,
          last_name: row.last_name,
          nickname: row.nickname,
          rolle: row.rolle,
          ressort: row.ressort,
          is_dancer: row.is_dancer ?? null,
          avatar_url: row.avatar_url,
        })),
      );
    },
  });

  const selectedQ = useQuery({
    queryKey: ['member-detail', selectedId ?? 'none'],
    enabled: !!selectedId,
    queryFn: async (): Promise<MemberDetail> => {
      const uid = selectedId!;
      const [ur, ures, upEvents, upCostumes, adm, prof] = await Promise.all([
        supabase.from('user_roles').select('role_id').eq('user_id', uid),
        supabase.from('user_ressorts').select('ressort_id').eq('user_id', uid),
        supabase
          .from('user_permissions')
          .select('permission')
          .eq('user_id', uid)
          .eq('permission', 'manage_events')
          .maybeSingle(),
        supabase
          .from('user_permissions')
          .select('permission')
          .eq('user_id', uid)
          .in('permission', ['manage_costumes', 'costume_manager'])
          .limit(1)
          .maybeSingle(),
        supabase.from('admins').select('user_id').eq('user_id', uid).maybeSingle(),
        supabase.from('profiles').select('is_dancer').eq('id', uid).maybeSingle(),
      ]);
      if (ur.error) throw ur.error;
      if (ures.error) throw ures.error;
      if (upEvents.error) throw upEvents.error;
      if (upCostumes.error) throw upCostumes.error;
      if (adm.error) throw adm.error;
      if (prof.error) throw prof.error;

      return {
        role_ids: ((ur.data ?? []) as Array<{ role_id: string }>).map((x) => String(x.role_id)),
        ressort_ids: ((ures.data ?? []) as Array<{ ressort_id: string }>).map((x) => String(x.ressort_id)),
        can_manage_events: !!upEvents.data,
        can_manage_costumes: !!upCostumes.data,
        is_admin: !!adm.data,
        is_dancer: ((prof.data as { is_dancer?: boolean | null } | null) ?? null)?.is_dancer ?? null,
      };
    },
  });

  const roleOptions = useMemo(
    () => (rolesQ.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [rolesQ.data],
  );
  const ressortOptions = useMemo(
    () => (ressortsQ.data ?? []).map((r) => ({ value: r.id, label: r.name })),
    [ressortsQ.data],
  );

  const filtered = useMemo(() => {
    const qq = norm(q);
    const list = membersQ.data ?? [];
    if (!qq) return list;
    return list.filter((m) => {
      const hay = norm(
        [m.first_name, m.last_name, m.nickname, m.rolle, m.ressort, m.id].filter(Boolean).join(' '),
      );
      return hay.includes(qq);
    });
  }, [membersQ.data, q]);

  const selectedMember = useMemo(
    () => (membersQ.data ?? []).find((m) => m.id === selectedId) ?? null,
    [membersQ.data, selectedId],
  );

  const currentRoleIds = draftRoleIds ?? selectedQ.data?.role_ids ?? [];
  const currentRessortIds = draftRessortIds ?? selectedQ.data?.ressort_ids ?? [];
  const currentCanManageEvents = draftCanManageEvents ?? selectedQ.data?.can_manage_events ?? false;
  const currentCanManageCostumes =
    draftCanManageCostumes ?? selectedQ.data?.can_manage_costumes ?? false;
  const currentIsAdmin = draftIsAdmin ?? selectedQ.data?.is_admin ?? false;
  const currentIsDancer = draftIsDancer ?? (selectedQ.data?.is_dancer ?? false);

  const saveM = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error('Kein Mitglied ausgewaehlt.');
      if (!selectedQ.data) throw new Error('Mitglied-Details sind noch nicht geladen.');

      const roleIds = draftRoleIds ?? selectedQ.data.role_ids;
      const ressortIds = draftRessortIds ?? selectedQ.data.ressort_ids;
      const canManage = draftCanManageEvents ?? selectedQ.data.can_manage_events;
      const canManageCostumes =
        draftCanManageCostumes ?? selectedQ.data.can_manage_costumes;
      const isAdmin = draftIsAdmin ?? selectedQ.data.is_admin;

      // Rollen/Ressorts (security definer RPC; admin-only)
      const { error: assignErr } = await supabase.rpc('set_user_assignments', {
        p_user_id: selectedId,
        p_role_ids: roleIds,
        p_ressort_ids: ressortIds,
      });
      if (assignErr) throw assignErr;

      // manage_events permission
      if (canManage) {
        const { error } = await supabase
          .from('user_permissions')
          .upsert({ user_id: selectedId, permission: 'manage_events' });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_permissions')
          .delete()
          .eq('user_id', selectedId)
          .eq('permission', 'manage_events');
        if (error) throw error;
      }

      // manage_costumes permission
      if (canManageCostumes) {
        const { error } = await supabase
          .from('user_permissions')
          .upsert({ user_id: selectedId, permission: 'manage_costumes' });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_permissions')
          .delete()
          .eq('user_id', selectedId)
          .in('permission', ['manage_costumes', 'costume_manager']);
        if (error) throw error;
      }

      // admin toggle
      if (isAdmin) {
        const { error } = await supabase.from('admins').upsert({ user_id: selectedId });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('admins').delete().eq('user_id', selectedId);
        if (error) throw error;
      }

      // profile flags
      if (draftIsDancer !== null) {
        const { error } = await supabase
          .from('profiles')
          .update({ is_dancer: draftIsDancer })
          .eq('id', selectedId);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      notifications.show({ color: 'teal', title: 'Mitglieder', message: 'Gespeichert.' });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['member-detail'] }),
        qc.invalidateQueries({ queryKey: ['members'] }),
      ]);
      await refreshCapabilities();
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Speichern fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  const inviteM = useMutation({
    mutationFn: async () => {
      const email = inviteEmail.trim().toLowerCase();
      if (!email) throw new Error('Bitte zuerst eine Email-Adresse eingeben.');
      const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!re.test(email)) throw new Error('Bitte eine gueltige Email-Adresse eingeben.');

      const redirectTo = `${window.location.origin}/auth/callback?next=/reset-password`;
      const { data, error } = await supabase.functions.invoke('admin-invite-member', {
        body: { email, redirect_to: redirectTo },
      });
      if (error) throw error;
      if (!data || data.ok !== true) {
        const msg = (data as { error?: string } | null)?.error ?? 'Einladung fehlgeschlagen.';
        throw new Error(msg);
      }
      return data as { email: string };
    },
    onSuccess: (data) => {
      notifications.show({
        color: 'teal',
        title: 'Einladung gesendet',
        message: `Invite-Mail wurde an ${data.email} versendet.`,
      });
      setInviteEmail('');
    },
    onError: (e: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Einladung fehlgeschlagen',
        message: (e as { message?: string } | null)?.message ?? String(e),
      });
    },
  });

  function resetDraft() {
    setDraftRoleIds(null);
    setDraftRessortIds(null);
    setDraftCanManageEvents(null);
    setDraftCanManageCostumes(null);
    setDraftIsAdmin(null);
    setDraftIsDancer(null);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Mitglieder"
        description="Rollen, Ressorts, Admin und Berechtigungen."
        loading={membersQ.isLoading || rolesQ.isLoading || ressortsQ.isLoading}
      />

      {capabilities.isAdmin ? (
        <Card
          radius="lg"
          p="md"
          className="premium-card"
        >
          <Stack gap="xs">
            <Text c="var(--color-text)" fw={700}>
              Neues Mitglied
            </Text>
            <Text c="dimmed" size="sm">
              Invite-Mail versenden (Passwort setzen erfolgt ueber den Link in der Email).
            </Text>
            <Group align="end">
              <TextInput
                label="Email"
                placeholder="mitglied@example.org"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                onClick={() => inviteM.mutate()}
                loading={inviteM.isPending}
                disabled={!inviteEmail.trim()}
              >
                Einladen
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : null}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card
          radius="lg"
          p="md"
          className="premium-card"
        >
          <Stack gap="sm">
            <TextInput
              placeholder="Suchen..."
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
            />

            <Divider />

            <ScrollArea h={520}>
              <Stack gap={6}>
                {filtered.length === 0 && !membersQ.isLoading && (
                  <EmptyState
                    icon={<IconSearch size={48} />}
                    title="Keine Mitglieder gefunden"
                    description={q ? `Keine Treffer für „${q}"` : 'Noch keine Mitglieder vorhanden.'}
                  />
                )}
                {filtered.map((m) => {
                  const active = m.id === selectedId;
                  return (
                    <Card
                      key={m.id}
                      radius="md"
                      p="sm"
                      style={{
                        cursor: 'pointer',
                        background: active ? 'rgba(69, 61, 181, 0.18)' : 'var(--app-surface-3)',
                        border: active
                          ? '1px solid rgba(69, 61, 181, 0.40)'
                          : '1px solid var(--app-border-2)',
                      }}
                      onClick={() => {
                        if (m.id === selectedId) return;
                        setSelectedId(m.id);
                        resetDraft();
                      }}
                    >
                      <Group justify="space-between" align="flex-start">
                        <Group gap="sm" align="center">
                          <Avatar src={m.avatar_url || undefined} radius="xl" size={34} color="brand">
                            {initials(m)}
                          </Avatar>
                          <Box>
                            <Text c="var(--color-text)" fw={700} size="sm">
                              {displayName(m)}
                            </Text>
                            <Text c="dimmed" size="xs">
                              {m.rolle || ''}
                              {m.rolle && m.ressort ? '  ' : ''}
                              {m.ressort || ''}
                            </Text>
                          </Box>
                        </Group>
                        <Group gap={6}>
                          {m.is_dancer ? (
                            <Badge color="pink" variant="light">
                              Tanz
                            </Badge>
                          ) : null}
                        </Group>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
            </ScrollArea>
          </Stack>
        </Card>

        <Card
          radius="lg"
          p="md"
          className="premium-card"
        >
          {!selectedId ? (
            <EmptyState
              icon={<IconUser size={48} />}
              title="Kein Mitglied ausgewählt"
              description="Wähle links ein Mitglied aus, um Rollen, Ressorts und Berechtigungen zu bearbeiten."
            />
          ) : (
            <Stack gap="sm">
              <Group justify="space-between">
                <Group gap="sm" align="center">
                  <Avatar
                    src={selectedMember?.avatar_url || undefined}
                    radius="xl"
                    size={44}
                    color="brand"
                  >
                    {selectedMember ? initials(selectedMember) : selectedId.slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Text c="var(--color-text)" fw={800}>
                      {selectedMember ? displayName(selectedMember) : selectedId}
                    </Text>
                    <Text c="dimmed" size="xs">
                      id: {selectedId}
                    </Text>
                  </Box>
                </Group>
                {selectedQ.isLoading && <Loader size="sm" />}
              </Group>

              <Divider />

              <MultiSelect
                label="Rollen"
                data={roleOptions}
                value={currentRoleIds}
                onChange={setDraftRoleIds}
                searchable
                clearable
                nothingFoundMessage="Keine Rolle"
                styles={{ label: { color: 'var(--color-text-muted)' } }}
              />

              <MultiSelect
                label="Ressorts"
                data={ressortOptions}
                value={currentRessortIds}
                onChange={setDraftRessortIds}
                searchable
                clearable
                nothingFoundMessage="Kein Ressort"
                styles={{ label: { color: 'var(--color-text-muted)' } }}
              />

              <Group justify="space-between" mt="xs">
                <Switch
                  label="Kann Events verwalten (manage_events)"
                  checked={currentCanManageEvents}
                  onChange={(e) => setDraftCanManageEvents(e.currentTarget.checked)}
                />
                <Switch
                  label="Kostüm-Manager (manage_costumes)"
                  checked={currentCanManageCostumes}
                  onChange={(e) => setDraftCanManageCostumes(e.currentTarget.checked)}
                />
              </Group>

              <Group justify="space-between" mt="xs">
                <Switch
                  label="Admin"
                  checked={currentIsAdmin}
                  onChange={(e) => {
                    const next = e.currentTarget.checked;
                    if (!next && selectedId === session?.user?.id) {
                      const ok = window.confirm(
                        'Du entfernst dir selbst Admin-Rechte. Danach verlierst du Zugriff auf diese Seite. Fortfahren?',
                      );
                      if (!ok) return;
                    }
                    setDraftIsAdmin(next);
                  }}
                />
              </Group>

              <Switch
                label="Taenzer (is_dancer)"
                checked={currentIsDancer === true}
                onChange={(e) => setDraftIsDancer(e.currentTarget.checked)}
              />

              <Group justify="flex-end" mt="md">
                <Button onClick={() => saveM.mutate()} loading={saveM.isPending} disabled={selectedQ.isLoading}>
                  Speichern
                </Button>
              </Group>
            </Stack>
          )}
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
